import React, { useState, useEffect } from 'react';
import { Search, FileText, User } from 'lucide-react';

function Contract() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Contract form data
  const [contractData, setContractData] = useState({
    summary: '',
    purpose: '본 계약서는 갑이 을에게 위탁한 개발 업무를 을이 수행하고, 이에 대한 대금을 갑이 지급하는 것을 목적으로 한다.',
    workContent: `을은 다음과 같은 업무를 수행한다.
\\begin{itemize}[leftmargin=2em]
    \\item add item
\\end{itemize}
업무 완료 기한은 \\textbf{${new Date().getFullYear()}년 ${new Date().getMonth() + 1}월 ${new Date().getDate()}일}이다.`,
    totalAmount: '',
    contractAmount: '',
    remainingAmount: '',
    contractDate: new Date().toISOString().split('T')[0],
    paymentRatio: '직접 입력'
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const [companiesResponse, projectsResponse] = await Promise.all([
        fetch(`${baseUrl}/api/companies`),
        fetch(`${baseUrl}/api/projects`)
      ]);
      
      const companiesData = await companiesResponse.json();
      const projectsData = await projectsResponse.json();
      
      // Create a map of company_id to their most recent contract date
      const companyLatestDates = {};
      projectsData.forEach(project => {
        if (project.contract_date && project.company_id) {
          const contractDate = new Date(project.contract_date);
          if (!companyLatestDates[project.company_id] || 
              contractDate > new Date(companyLatestDates[project.company_id])) {
            companyLatestDates[project.company_id] = project.contract_date;
          }
        }
      });
      
      // Sort companies by most recent contract date, then by created_at as fallback
      const sortedCompanies = companiesData.sort((a, b) => {
        const dateA = companyLatestDates[a.id] ? new Date(companyLatestDates[a.id]) : new Date(a.created_at);
        const dateB = companyLatestDates[b.id] ? new Date(companyLatestDates[b.id]) : new Date(b.created_at);
        return dateB - dateA; // Descending order
      });
      
      setClients(sortedCompanies);
      setFilteredClients(sortedCompanies);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search term
  useEffect(() => {
    if (!clientSearchTerm) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        (client.client_name || '').toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
        (client.company_name || '').toLowerCase().includes(clientSearchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  }, [clients, clientSearchTerm]);

  const openClientModal = () => {
    setShowClientModal(true);
  };

  const closeClientModal = () => {
    setShowClientModal(false);
    setClientSearchTerm('');
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    closeClientModal();
  };

  const generateContract = async () => {
    if (!selectedClient) {
      alert('클라이언트를 선택해주세요.');
      return;
    }

    const formattedDate = contractData.contractDate
      ? new Date(contractData.contractDate).toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : new Date().toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

    const isBusiness = selectedClient.is_business;
    const totalAmount = parseInt(contractData.totalAmount.replace(/,/g, '')) || 0;
    const contractAmount = parseInt(contractData.contractAmount.replace(/,/g, '')) || 0;
    const remainingAmount = parseInt(contractData.remainingAmount.replace(/,/g, '')) || 0;

    const latexTemplate = `\\documentclass[12pt]{article}
\\usepackage{kotex}
\\usepackage[a4paper, margin=2.5cm]{geometry}
\\usepackage{setspace}
\\usepackage{enumitem}

\\setstretch{1.5}

\\title{\\Huge \\bfseries 외주 계약서}
\\date{}
\\begin{document}

\\maketitle

\\noindent
${isBusiness ? `${selectedClient.company_name}(대표: ${selectedClient.client_name}, 사업자등록번호: ${selectedClient.business_number})` : `${selectedClient.client_name}(주민등록번호: ${selectedClient.business_number})`}(이하 "갑")과 루프 디멘션(대표: 이정규, 사업자등록번호: 687-49-00844, 이하 "을")은 ${contractData.summary}와 관련하여 다음과 같이 합의한다.

\\vspace{1em}
\\section*{제1조 (계약의 목적)}
${contractData.purpose}

\\section*{제2조 (업무 내용)}
${contractData.workContent}

\\section*{제3조 (대금 지급 방법)}
\\begin{itemize}[leftmargin=2em]
    \\item 총액: ${totalAmount.toLocaleString()}원 (부가세 별도)
    \\item 지급 방법: 계약금 및 잔금 분할 지급
    \\begin{itemize}[leftmargin=2em]
        \\item 계약금: ${contractAmount.toLocaleString()}원 (계약 체결일과 동시에 지급)
        \\item 잔금: ${remainingAmount.toLocaleString()}원 (최종 완료일에 지급)
    \\end{itemize}
    \\item 지급 계좌: 국민은행 35880204456838
\\end{itemize}

\\section*{제4조 (계약 목적물)}
본 계약에 따라 개발된 소스코드를 포함한 모든 개발 결과물의 지적 재산권 및 소유권은 \\textbf{을이 결과물을 인도하고 갑이 용역비를 완납한 시점}에 갑에게 귀속된다.

\\section*{제5조 (추가 비용 처리)}
업무 진행 중 발생한 추가 비용은 \\textbf{사전 협의 후} 갑이 부담한다.

\\section*{제6조 (업무 진행 및 보고)}
을은 업무 진행 상황을 갑이 요청 시 보고하여야 한다.

\\section*{제7조 (검수 및 수정)}
\\begin{enumerate}[label=\\arabic*.]
    \\item 갑은 을로부터 결과물을 공급받은 후 1주일 이내에 검수를 진행하고, 하자가 있는 경우 지체 없이 통지한다.
    \\item 을은 지적된 하자를 정해진 기한 내에 수정하고 재납품하여야 한다.
\\end{enumerate}

\\section*{제8조 (비밀 유지)}
\\begin{enumerate}[label=\\arabic*.]
    \\item 을은 본 계약 이행 중 알게 된 갑의 기밀 정보를 제3자에게 누설하지 않는다.
    \\item 이 의무는 계약 종료 후에도 유효하다.
\\end{enumerate}

\\section*{제9조 (작업 장소)}
본 업무는 비대면으로 진행되며, 별도 상호 협의하에 을의 작업 장소에서 수행한다.

\\section*{제10조 (유지보수)}
\\begin{itemize}[leftmargin=2em]
    \\item 본 계약은 유지보수를 포함하지 않는다.
    \\item 단, 납품된 결과물에 명백한 하자가 있어 시스템 운영에 지장이 발생한 경우, 을은 이를 A/S하여야 한다.
\\end{itemize}

\\section*{제11조 (권리 침해 보증)}
\\begin{itemize}[leftmargin=2em]
    \\item 을은 개발된 결과물이 제3자의 권리를 침해하지 않음을 보증한다.
    \\item 만약 분쟁이 발생할 경우, 귀책사유에 따라 비용을 분담하여 성실히 해결한다.
\\end{itemize}

\\section*{제12조 (계약 해제)}
다음 각 호의 경우, 사전 통지 없이 계약을 해제하고 손해배상을 청구할 수 있다:
\\begin{enumerate}[label=\\arabic*.]
    \\item 계약 위반 또는 중대한 배신행위
    \\item 수표, 어음의 부도
    \\item 강제집행 대상
    \\item 파산, 회생 신청
    \\item 영업 양도, 합병, 해산
    \\item 행정처분(영업취소 등)
    \\item 계약 이행 곤란 사유 발생
\\end{enumerate}

\\section*{제13조 (협의)}
본 계약에 명시되지 않은 사항은 \\textbf{상호 협의}를 통해 결정한다.

\\section*{제14조 (계약의 효력)}
본 계약의 효력은 \\textbf{${formattedDate}} 계약 체결일부터 발생하며, 계약서 2부를 작성하여 갑과 을이 각 1부씩 보관한다.

\\vspace{2em}
\\begin{flushleft}
\\textbf{${formattedDate}} \\\\
\\vspace{1em}
\\textbf{'갑'} \\\\
${isBusiness ? `상호: ${selectedClient.company_name} \\\\
대표자: ${selectedClient.client_name}` : `이름: ${selectedClient.client_name}`} \\\\
주소: ${selectedClient.address || '-'} \\\\
(서명 또는 인)

\\vspace{2em}
\\textbf{'을'} \\\\
상호: 루프 디멘션 \\\\
대표자: 이정규 \\\\
주소: 서울시 서초동 \\\\
(서명 또는 인)
\\end{flushleft}

\\end{document}`;

  try {
    // Generate and download the PDF
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const response = await fetch(`${baseUrl}/api/generate-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        latexContent: latexTemplate,
        filename: `contract_${selectedClient.client_name}_${formattedDate.replace(/[^0-9]/g, '')}`
      })
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${selectedClient.client_name}_${formattedDate.replace(/[^0-9]/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('계약서가 생성되었습니다!');
    } else {
      alert('PDF 생성에 실패했습니다.');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('PDF 생성 중 오류가 발생했습니다.');
  }
};

  const handleInputChange = (field, value) => {
    setContractData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate contract and remaining amounts based on payment ratio
      if (field === 'totalAmount' || field === 'paymentRatio') {
        const total = parseInt(newData.totalAmount.replace(/,/g, '')) || 0;
        
        if (newData.paymentRatio === '3/7') {
          newData.contractAmount = formatNumber(Math.floor(total * 0.3).toString());
          newData.remainingAmount = formatNumber(Math.floor(total * 0.7).toString());
        } else if (newData.paymentRatio === '4/6') {
          newData.contractAmount = formatNumber(Math.floor(total * 0.4).toString());
          newData.remainingAmount = formatNumber(Math.floor(total * 0.6).toString());
        } else if (newData.paymentRatio === '5/5') {
          newData.contractAmount = formatNumber(Math.floor(total * 0.5).toString());
          newData.remainingAmount = formatNumber(Math.floor(total * 0.5).toString());
        }
      }
      
      return newData;
    });
  };

  const formatNumber = (value) => {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>계약서</h1>
        <p>계약서 생성 및 관리</p>
      </div>

      <div className="controls">
        <button className="btn btn-primary btn-large" onClick={openClientModal} style={{ fontSize: '1.1rem', padding: '1rem 2rem' }}>
          <User size={20} />
          클라이언트 선택
        </button>
      </div>

      {selectedClient && (
        <div className="contract-form">
          <div className="selected-client">
            <h3>선택된 클라이언트</h3>
            <div className="client-info">
              <div className="client-details">
                <span className="client-name">{selectedClient.client_name}</span>
                {selectedClient.company_name && (
                  <span className="company-name"> - {selectedClient.company_name}</span>
                )}
              </div>
              <div className="client-meta">
                <span className={`medium-badge ${selectedClient.medium || 'default'}`}>
                  {selectedClient.medium || '기타'}
                </span>
                <span className={`business-badge ${selectedClient.is_business ? '사업자' : '개인'}`}>
                  {selectedClient.is_business ? '사업자' : '개인'}
                </span>
                {selectedClient.business_number && (
                  <span className="business-number">
                    {selectedClient.is_business ? '사업자번호' : '주민번호'}: {selectedClient.business_number}
                  </span>
                )}
                {selectedClient.phone && (
                  <span className="phone">📞 {selectedClient.phone}</span>
                )}
                {selectedClient.email && (
                  <span className="email">✉️ {selectedClient.email}</span>
                )}
                <span className="address">📍 {selectedClient.address || '-'}</span>
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group full-width">
              <label className="form-label">요약</label>
              <input
                type="text"
                className="form-input"
                value={contractData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                placeholder="예: 어플리케이션 내 기능 구현을 위한 API 개발"
              />
            </div>

            <div className="form-group full-width">
              <label className="form-label">목적</label>
              <input
                type="text"
                className="form-input"
                value={contractData.purpose}
                onChange={(e) => handleInputChange('purpose', e.target.value)}
                placeholder="계약 목적을 입력하세요"
              />
            </div>

            <div className="form-group full-width">
              <label className="form-label">업무 내용 (LaTeX 형식)</label>
              <textarea
                className="form-input"
                rows="8"
                value={contractData.workContent}
                onChange={(e) => handleInputChange('workContent', e.target.value)}
                placeholder="업무 내용을 LaTeX 형식으로 입력하세요"
              />
            </div>

            <div className="form-group">
              <label className="form-label">총액</label>
              <input
                type="text"
                className="form-input"
                value={contractData.totalAmount}
                onChange={(e) => handleInputChange('totalAmount', formatNumber(e.target.value))}
                placeholder="1,900,000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">지급 비율</label>
              <select
                value={contractData.paymentRatio}
                onChange={(e) => handleInputChange('paymentRatio', e.target.value)}
                className="form-input"
              >
                <option value="직접 입력">직접 입력</option>
                <option value="3/7">3/7 (30% / 70%)</option>
                <option value="4/6">4/6 (40% / 60%)</option>
                <option value="5/5">5/5 (50% / 50%)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">계약금</label>
              <input
                type="text"
                className="form-input"
                value={contractData.contractAmount}
                onChange={(e) => handleInputChange('contractAmount', formatNumber(e.target.value))}
                placeholder="950,000"
                disabled={contractData.paymentRatio !== '직접 입력'}
              />
            </div>

            <div className="form-group">
              <label className="form-label">잔금</label>
              <input
                type="text"
                className="form-input"
                value={contractData.remainingAmount}
                onChange={(e) => handleInputChange('remainingAmount', formatNumber(e.target.value))}
                placeholder="950,000"
                disabled={contractData.paymentRatio !== '직접 입력'}
              />
            </div>

            <div className="form-group">
              <label className="form-label">계약일</label>
              <input
                type="date"
                className="form-input"
                value={contractData.contractDate}
                onChange={(e) => handleInputChange('contractDate', e.target.value)}
              />
            </div>
          </div>

          <div className="contract-generation-section">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={generateContract}
            >
              <FileText size={16} />
              계약서 생성 (PDF)
            </button>
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showClientModal && (
        <div className="form-overlay client-selection-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h2>클라이언트 선택</h2>
              <button onClick={closeClientModal} className="btn-icon">×</button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="클라이언트명 또는 상호명으로 검색..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="form-input"
                  autoFocus
                />
              </div>
              <div className="client-list">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="client-item"
                    onClick={() => selectClient(client)}
                  >
                    <div className="client-info">
                      <div className="client-details">
                        <span className="client-name">{client.client_name}</span>
                        {client.company_name && (
                          <span className="company-name"> - {client.company_name}</span>
                        )}
                      </div>
                      <div className="client-meta">
                        <span className={`medium-badge ${client.medium || 'default'}`}>
                          {client.medium || '기타'}
                        </span>
                        <span className={`business-badge ${client.is_business ? '사업자' : '개인'}`}>
                          {client.is_business ? '사업자' : '개인'}
                        </span>
                        {client.business_number && (
                          <span className="business-number">
                            {client.is_business ? '사업자번호' : '주민번호'}: {client.business_number}
                          </span>
                        )}
                        {client.phone && (
                          <span className="phone">📞 {client.phone}</span>
                        )}
                        {client.email && (
                          <span className="email">✉️ {client.email}</span>
                        )}
                        <span className="address">📍 {client.address || '-'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contract; 